import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Instagram, MessageCircle } from "lucide-react";

export type ChatChannel = "whatsapp" | "instagram" | "messenger";

const CHANNELS: ChatChannel[] = ["whatsapp", "instagram"];

const CHANNEL_META: Record<
  ChatChannel,
  { label: string; headerClass: string; agentBubble: string; accent: string; senderLabelClass: string }
> = {
  whatsapp: {
    label: "WhatsApp Business",
    headerClass: "bg-[#075E54] text-[#ECE5DD]",
    agentBubble: "bg-[#DCF8C6] text-[#111111] rounded-2xl rounded-tr-sm",
    accent: "#25D366",
    senderLabelClass: "text-[#667781]",
  },
  instagram: {
    label: "Instagram",
    headerClass: "bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white",
    agentBubble: "bg-[#EFEFEF] text-black rounded-3xl",
    accent: "#E1306C",
    senderLabelClass: "text-white/60",
  },
  messenger: {
    label: "Messenger",
    headerClass: "bg-[#0084FF] text-white",
    agentBubble: "bg-[#E5F0FF] text-black rounded-3xl",
    accent: "#0084FF",
    senderLabelClass: "text-gray-400",
  },
};

type Msg = { id: string; side: "left" | "right"; text: string; time: string; type?: "text" | "audio" };

const CONVERSATIONS: Msg[][] = [
  [
    {
      id: "1",
      side: "left",
      text: "Oi! Vi o site da FabChat. Quero saber se vocês agendam demo?",
      time: "09:12",
    },
    {
      id: "2",
      side: "right",
      text: "Olá! Consigo sim, qual dia fica melhor para você?",
      time: "09:12",
    },
    {
      id: "3",
      side: "left",
      text: "Amanhã à tarde, se tiver vaga.",
      time: "09:13",
    },
    {
      id: "4",
      side: "right",
      text: "Tenho amanhã às 14h ou quinta às 10h. Qual prefere?",
      time: "09:13",
    },
    {
      id: "5",
      side: "left",
      text: "14h amanhã está ótimo.",
      time: "09:14",
    },
    {
      id: "6",
      side: "right",
      text: "Feito! Agendado para amanhã às 14h. Aqui está o link do Google Meet: meet.google.com/abc-defg-hij",
      time: "09:14",
    },
  ],
  [
    {
      id: "d1",
      side: "left",
      text: "Boa tarde! Gostaria de marcar uma avaliação com a Dra. Ana.",
      time: "14:20",
    },
    {
      id: "d2",
      side: "right",
      text: "Olá! Temos horários para amanhã às 10h ou sexta às 16h. Qual fica melhor?",
      time: "14:20",
    },
    {
      id: "d3",
      side: "left",
      text: "Audio message",
      time: "14:22",
      type: "audio",
    },
    {
      id: "d4",
      side: "right",
      text: "Entendido! Agendado para sexta às 16h, pois fica melhor após o seu trabalho. Enviarei um lembrete no dia!",
      time: "14:23",
    },
  ],
  [
    {
      id: "i1",
      side: "left",
      text: "Oi! Vi o anúncio do apê de 2 quartos no Centro. Ainda está disponível?",
      time: "16:40",
    },
    {
      id: "i2",
      side: "right",
      text: "Olá! Sim, o apartamento na Rua das Flores continua disponível. Você busca para morar ou investir?",
      time: "16:40",
    },
    {
      id: "i3",
      side: "left",
      text: "Para morar. Sou a Marina.",
      time: "16:41",
    },
    {
      id: "i4",
      side: "right",
      text: "Perfeito, Marina! Posso agendar visita sábado às 11h ou segunda às 18h. Qual horário prefere?",
      time: "16:41",
    },
    {
      id: "i5",
      side: "left",
      text: "Sábado às 11h funciona!",
      time: "16:42",
    },
    {
      id: "i6",
      side: "right",
      text: "Agendado! Sábado às 11h — enviei a confirmação e o endereço completo aqui no chat.",
      time: "16:42",
    },
  ],
];

type NotifKind = "agendamento" | "humano" | "encerrado" | null;

const NOTIF_TEXT: Record<Exclude<NotifKind, null>, string> = {
  agendamento: "+1 agendamento",
  humano: "Lead esperando atendimento humano",
  encerrado: "Lead atendido e atendimento encerrado",
};

const MSG_STEP_MS = 4200;
const MSG_STEP_CLIENT_MS = 2800;
const LOOP_PAUSE_MS = 9000;
const TYPING_MS_PER_CHAR = 46;
const MOCK_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Papel de parede do chat — cores sólidas fiéis aos apps, sem padrão SVG quebrado */
function ChatWallpaper({ channel }: { channel: ChatChannel }) {
  if (channel === "whatsapp") {
    return (
      <div className="pointer-events-none absolute inset-0 bg-[#E5DDD5]" aria-hidden>
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' fill='%23E5DDD5'/%3E%3Cg fill='%23C5B9A8' fill-opacity='0.55'%3E%3Ccircle cx='12' cy='14' r='1.2'/%3E%3Ccircle cx='38' cy='8' r='0.9'/%3E%3Ccircle cx='62' cy='22' r='1'/%3E%3Ccircle cx='22' cy='44' r='0.8'/%3E%3Ccircle cx='54' cy='52' r='1.1'/%3E%3Ccircle cx='8' cy='68' r='0.9'/%3E%3Ccircle cx='70' cy='64' r='1'/%3E%3C/g%3E%3C/svg%3E\")",
            backgroundSize: "80px 80px",
          }}
        />
      </div>
    );
  }

  if (channel === "instagram") {
    return <div className="pointer-events-none absolute inset-0 bg-[#0B1014]" aria-hidden />;
  }

  return <div className="pointer-events-none absolute inset-0 bg-white" aria-hidden />;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function ChannelIcon({ channel }: { channel: ChatChannel }) {
  if (channel === "whatsapp") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="currentColor" aria-hidden>
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.881 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );
  }
  if (channel === "instagram") {
    return <Instagram className="h-5 w-5 shrink-0" aria-hidden />;
  }
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="currentColor" aria-hidden>
      <path d="M12 0C5.176 0 0 4.965 0 11.53c0 3.518 1.53 6.786 4.184 8.948v3.522l3.82-2.115c1.264.35 2.6.541 3.996.541 6.824 0 12-4.966 12-11.53C24 4.965 18.824 0 12 0zm1.188 15.698-3.033-3.23-5.938 3.23 6.518-6.93 3.12 3.23 5.845-3.23-6.512 6.93z" />
    </svg>
  );
}

function TypingEffect({ text }: { text: string }) {
  const [displayed, setDisplayed] = useState("");

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplayed(text);
      return;
    }

    let i = 0;
    setDisplayed("");
    const interval = window.setInterval(() => {
      i += 1;
      setDisplayed(text.slice(0, i));
      const scrollContainer = document.getElementById("mockup-chat-scroll");
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
      if (i >= text.length) window.clearInterval(interval);
    }, TYPING_MS_PER_CHAR);

    return () => window.clearInterval(interval);
  }, [text]);

  return <span className="whitespace-pre-wrap break-words">{displayed}</span>;
}

function MessageBody({ msg, channel }: { msg: Msg; channel: ChatChannel }) {
  if (msg.type === "audio") {
    return (
      <div className="flex min-w-[140px] items-center gap-2 pb-1 pt-1">
        <div
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            msg.side === "left" ? "bg-black/5" : "bg-black/10",
          )}
        >
          <div
            className={cn(
              "ml-1 h-0 w-0 border-b-[5px] border-l-[8px] border-t-[5px] border-b-transparent border-t-transparent opacity-70",
              msg.side === "left" ? "border-l-black" : "border-l-current",
            )}
          />
        </div>
        <div className="flex flex-1 items-center gap-0.5 opacity-60">
          <div className="h-2 w-1 rounded-full bg-current" />
          <div className="h-3 w-1 rounded-full bg-current" />
          <div className="h-5 w-1 rounded-full bg-current" />
          <div className="h-4 w-1 rounded-full bg-current" />
          <div className="h-6 w-1 rounded-full bg-current" />
        </div>
        <span className="ml-1 text-[10px] font-medium opacity-60">0:12</span>
      </div>
    );
  }

  if (msg.side === "left" || prefersReducedMotion()) {
    return <span className="whitespace-pre-wrap break-words">{msg.text}</span>;
  }

  return <TypingEffect text={msg.text} />;
}

function bubbleClasses(channel: ChatChannel, side: Msg["side"]) {
  if (side === "left") {
    if (channel === "instagram") {
      return "rounded-3xl bg-[#262626] text-white";
    }
    if (channel === "messenger") {
      return "rounded-3xl bg-[#E4E6EB] text-black";
    }
    return "rounded-2xl rounded-tl-sm border border-black/[0.04] bg-white text-[#111111] shadow-[0_1px_1px_rgba(0,0,0,0.08)]";
  }

  if (channel === "instagram") {
    return "rounded-3xl bg-[#8A3AB9] text-white";
  }
  if (channel === "messenger") {
    return "rounded-3xl bg-[#0084FF] text-white";
  }
  return "rounded-2xl rounded-tr-sm border border-black/[0.04] bg-[#DCF8C6] text-[#111111] shadow-[0_1px_1px_rgba(0,0,0,0.06)]";
}

function delayAfterMessage(msg: Msg) {
  if (msg.side === "left") return MSG_STEP_CLIENT_MS;
  if (prefersReducedMotion()) return MSG_STEP_MS;
  return Math.max(MSG_STEP_MS, msg.text.length * TYPING_MS_PER_CHAR + 1200);
}

function FloatingNotif({
  active,
  className,
  title,
  body,
}: {
  active: boolean;
  className?: string;
  title: string;
  body: string;
}) {
  return (
    <div
      className={cn(
        "absolute z-30 w-[168px] motion-safe:transition-[opacity,transform] motion-safe:duration-[450ms] motion-reduce:transition-none",
        active
          ? "translate-y-0 scale-100 opacity-100"
          : "pointer-events-none translate-y-2 scale-[0.98] opacity-0",
        className,
      )}
      style={{ transitionTimingFunction: MOCK_EASE }}
    >
      <div className="rounded-2xl border border-white/20 bg-white/[0.94] px-3 py-2 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.35)] backdrop-blur-sm">
        <p className="text-[9px] font-semibold uppercase tracking-wide text-foreground/45">{title}</p>
        <p className="mt-0.5 text-[11px] font-medium leading-snug text-foreground">{body}</p>
      </div>
    </div>
  );
}

export function HeroPhoneMockup() {
  const [channelIdx, setChannelIdx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [notif, setNotif] = useState<NotifKind>(null);
  const [animCycle, setAnimCycle] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);

  const channel = CHANNELS[channelIdx];
  const meta = CHANNEL_META[channel];
  const currentConversation = CONVERSATIONS[animCycle % CONVERSATIONS.length];

  useEffect(() => {
    if (visibleCount < currentConversation.length) {
      const prevMsg = visibleCount > 0 ? currentConversation[visibleCount - 1] : undefined;
      const waitMs = prevMsg ? delayAfterMessage(prevMsg) : 500;
      const t = window.setTimeout(() => setVisibleCount((c) => c + 1), waitMs);
      return () => window.clearTimeout(t);
    }

    const t1 = window.setTimeout(() => setNotif("agendamento"), 1400);
    const t2 = window.setTimeout(() => setNotif("humano"), 4200);
    const t3 = window.setTimeout(() => setNotif("encerrado"), 7000);
    const t4 = window.setTimeout(() => {
      setIsFlipping(true);
      window.setTimeout(() => {
        setChannelIdx((i) => (i + 1) % CHANNELS.length);
        setAnimCycle((c) => c + 1);
        setVisibleCount(0);
        setNotif(null);
        setIsFlipping(false);
      }, 420);
    }, LOOP_PAUSE_MS);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
      window.clearTimeout(t4);
    };
  }, [visibleCount, currentConversation.length]);

  const visibleMessages = useMemo(
    () => currentConversation.slice(0, visibleCount),
    [visibleCount, currentConversation],
  );

  useEffect(() => {
    const scrollContainer = document.getElementById("mockup-chat-scroll");
    if (!scrollContainer) return;
    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }, [visibleCount, visibleMessages]);

  return (
    <div className="relative mx-auto flex h-[480px] w-full max-w-[340px] items-center justify-center lg:max-w-[360px]">
      <FloatingNotif
        active={notif === "agendamento"}
        className="-right-4 top-[15%] sm:-right-8"
        title="FabChat"
        body={NOTIF_TEXT.agendamento}
      />

      <FloatingNotif
        active={notif === "humano"}
        className="-left-6 top-[40%] sm:-left-12"
        title="FabChat"
        body={NOTIF_TEXT.humano}
      />

      <FloatingNotif
        active={notif === "encerrado"}
        className="-right-4 bottom-[20%] sm:-right-10"
        title="FabChat"
        body={NOTIF_TEXT.encerrado}
      />

      <div
        className={cn(
          "relative z-10 h-[480px] w-[240px] rounded-[2.25rem] bg-gradient-to-b from-[#3a3a3c] via-[#1c1c1e] to-black p-[3px]",
          "shadow-[0_24px_48px_-12px_rgba(0,0,0,0.45)] motion-safe:transition-[opacity,transform] motion-safe:duration-500 motion-reduce:transition-none",
          isFlipping ? "scale-[0.98] opacity-0" : "scale-100 opacity-100",
        )}
        style={{ transitionTimingFunction: MOCK_EASE }}
      >
        <div className="relative h-full w-full overflow-hidden rounded-[2rem] bg-black">
          <div
            className="pointer-events-none absolute left-1/2 top-[8px] z-30 h-[22px] w-[72px] -translate-x-1/2 rounded-full bg-black"
            aria-hidden
          />

          <div className="absolute inset-[2px] flex flex-col overflow-hidden rounded-[1.85rem] bg-black">
            <div className={cn("relative z-20 shrink-0 motion-safe:transition-colors motion-safe:duration-500", meta.headerClass)}>
              <div className="h-[30px] shrink-0" aria-hidden />
              <div className="flex min-h-[36px] items-center gap-1.5 px-3 pb-2 text-[13px] font-medium">
                <ChannelIcon channel={channel} />
                <span className="min-w-0 flex-1 truncate tracking-tight">{meta.label}</span>
                <MessageCircle className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
              </div>
            </div>

            <div
              id="mockup-chat-scroll"
              className="relative z-10 flex w-full flex-1 flex-col overflow-y-auto motion-safe:transition-colors motion-safe:duration-500 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <ChatWallpaper channel={channel} />
              <div className="relative z-[1] flex min-h-full flex-col justify-end gap-2 px-3 pb-8 pt-3">
                {visibleMessages.map((m) => (
                  <div
                    key={`${animCycle}-${m.id}`}
                    className={cn("flex w-full", m.side === "right" ? "justify-end" : "justify-start")}
                  >
                    <div className="max-w-[88%] motion-safe:animate-chat-bubble-in motion-reduce:animate-none">
                      <div
                        className={cn(
                          "relative px-3 py-1.5 text-[12px] leading-[1.45]",
                          bubbleClasses(channel, m.side),
                        )}
                      >
                        <MessageBody msg={m} channel={channel} />
                        <span
                          className={cn(
                            "mt-1 block text-right text-[9px] leading-none",
                            m.side === "right" && channel === "whatsapp"
                              ? "text-[#667781]"
                              : m.side === "left" && channel === "whatsapp"
                                ? "text-[#667781]"
                                : "text-white/50",
                          )}
                        >
                          {m.time}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
